import { AgeType } from "libs/cat-calculations/src/lib/cat-calculations.types";
import { Recipe, RecipesForCatResponse } from "@forms/schema";
import type { TRecipeItem } from "@./suf-store";

import { Allergen } from "@/primary-models";

export type RecipeCount = 7 | 14 | 28;

type RecipeAllocation = Partial<Record<Recipe, number>>;
type AllergyScenario = "NO_ALLERGY" | "BEEF" | "TURKEY" | "DUCK" | "CHICKEN" | "SALMON" | "CHICKEN_FISH";
const FRESH_RECIPES: Recipe[] = [
  "CLUCK", "GOBBLE", "QUACK", "MOOO", "OINK", "BAAA", "SWISH", "SPLASH", "SHREDDED_CLUCK", "SHREDDED_GOBBLE", "SMOOTH_CLUCK", "SMOOTH_PECK",
];

export const getAvailableFreshRecipes = (): Recipe[] => FRESH_RECIPES;

/**
 * Recipe allocations organised by tray size, then by allergy scenario.
 *
 * Shredded variants follow the same allergy rules as their base recipes
 * (e.g. `SHREDDED_CLUCK` is treated like `CLUCK` for allergy purposes).
 */
const ALLOCATIONS: Record<RecipeCount, Record<AllergyScenario, RecipeAllocation>> = {
  7: {
    NO_ALLERGY: { CLUCK: 4, GOBBLE: 2, QUACK: 1 },
    BEEF: { CLUCK: 4, GOBBLE: 2, QUACK: 1 },
    TURKEY: { CLUCK: 4, QUACK: 2, MOOO: 1 },
    DUCK: { CLUCK: 4, GOBBLE: 2, MOOO: 1 },
    CHICKEN: { OINK: 4, MOOO: 2, SPLASH: 1 },
    SALMON: { CLUCK: 4, GOBBLE: 3 },
    CHICKEN_FISH: { MOOO: 7 },
  },
  14: {
    NO_ALLERGY: { CLUCK: 6, SHREDDED_CLUCK: 3, GOBBLE: 2, SHREDDED_GOBBLE: 1, QUACK: 1, MOOO: 1 },
    BEEF: { CLUCK: 6, SHREDDED_CLUCK: 3, GOBBLE: 2, SHREDDED_GOBBLE: 1, QUACK: 2 },
    TURKEY: { CLUCK: 7, SHREDDED_CLUCK: 3, QUACK: 2, MOOO: 2 },
    DUCK: { CLUCK: 6, SHREDDED_CLUCK: 3, GOBBLE: 2, SHREDDED_GOBBLE: 1, MOOO: 2 },
    CHICKEN: { OINK: 7, MOOO: 4, SPLASH: 3 },
    SALMON: { CLUCK: 9, GOBBLE: 5 },
    CHICKEN_FISH: { MOOO: 14 },
  },
  28: {
    NO_ALLERGY: { CLUCK: 12, SHREDDED_CLUCK: 6, GOBBLE: 4, SHREDDED_GOBBLE: 2, QUACK: 2, MOOO: 2 },
    BEEF: { CLUCK: 12, SHREDDED_CLUCK: 6, GOBBLE: 4, SHREDDED_GOBBLE: 2, QUACK: 4 },
    TURKEY: { CLUCK: 14, SHREDDED_CLUCK: 6, QUACK: 4, MOOO: 4 },
    DUCK: { CLUCK: 12, SHREDDED_CLUCK: 6, GOBBLE: 4, SHREDDED_GOBBLE: 2, MOOO: 4 },
    CHICKEN: { OINK: 14, MOOO: 8, SPLASH: 6 },
    SALMON: { CLUCK: 18, GOBBLE: 10 },
    CHICKEN_FISH: { MOOO: 28 },
  },
};

/**
 * Default recipe distributions for each tray size (no allergies).
 */
const DEFAULT_RECIPES: Record<RecipeCount, RecipeAllocation> = {
  7: { CLUCK: 4, GOBBLE: 2, QUACK: 1 }, // If this goes live again, will need to confirm what allocations should be
  14: {
    CLUCK: 6, GOBBLE: 2, QUACK: 1, MOOO: 1, SHREDDED_CLUCK: 1, SHREDDED_GOBBLE: 1, SMOOTH_PECK: 1, SWISH: 1,
  },
  28: {
    CLUCK: 12, GOBBLE: 4, QUACK: 2, MOOO: 2, SHREDDED_CLUCK: 2, SHREDDED_GOBBLE: 2, SMOOTH_PECK: 2, SWISH: 2,
  },
};

export const getDisallowedRecipes = (lifeStage: AgeType | undefined): Recipe[] => {
  if (lifeStage === "kitten") {
    return [ "BAAA" ];
  }
  return [];
};

export const transformRecipeItemsToRecipeMap = (
  recipeItems: TRecipeItem[],
  expectedCount?: RecipeCount,
): Map<Recipe, number> => {
  const recipeMap = new Map<Recipe, number>();
  const availableRecipes = new Set(getAvailableFreshRecipes());
  recipeItems.forEach((item) => {
    if (!availableRecipes.has(item.type)) {
      return;
    }
    recipeMap.set(item.type, item.quantity);
  });

  // Fall back to the default recipes so the user sees a valid starting selection.
  // This is a safety net in case filtering removes recipes and the total drops
  // below the expected pouch count (e.g. a recipe was discontinued).
  // We don't want to serve an incomplete box and make the user fix this themselves.
  if (expectedCount) {
    const totalAfterFilter = Array.from(recipeMap.values())
      .reduce((sum, qty) => sum + qty, 0);
    if (totalAfterFilter < expectedCount) {
      return getDefaultRecipes(expectedCount);
    }
  }

  return recipeMap;
};

export const getDefaultRecipes = (recipeCount: RecipeCount = 14): Map<Recipe, number> => {
  const recipeMap = new Map<Recipe, number>();
  Object.entries(DEFAULT_RECIPES[recipeCount]).forEach(([ recipe, quantity ]) => {
    if (quantity !== undefined && quantity > 0) {
      recipeMap.set(recipe as Recipe, quantity);
    }
  });

  return recipeMap;
};

export const getFreshTrialRecipes = (
  allergies: Allergen[],
  lifeStage: AgeType,
  recipesItems: TRecipeItem[] | undefined = undefined,
  recipeCount: RecipeCount = 14,
): RecipesForCatResponse => {
  const recipes = recipesItems
    ? transformRecipeItemsToRecipeMap(recipesItems, recipeCount)
    : getDefaultRecipes(recipeCount);
  return calculateRecipesForCat(recipes, allergies, getDisallowedRecipes(lifeStage), recipeCount);
};

export const calculateRecipesForCat = (
  defaultRecipeWeightings: Map<Recipe, number>,
  allergies: Allergen[],
  ignoreRecipes: Recipe[],
  recipeCount: RecipeCount = 14,
): RecipesForCatResponse => {
  const recipesNotApplicableForCat = allergies?.map((allergy) => allergyRecipeRecord[allergy]);
  const reduced = recipesNotApplicableForCat?.length > 0
    ? recipesNotApplicableForCat.reduce<Set<Recipe>>(
      (prev, cur) => new Set([ ...cur, ...prev ]),
      new Set<Recipe>(),
    )
    : new Set<Recipe>();
  ignoreRecipes.forEach((ir) => reduced.add(ir));
  const recipeQuantities = calculateRecipeQuantities(
    defaultRecipeWeightings,
    reduced,
    recipeCount,
  );

  const result = {
    allowedRecipesAndQuantities: recipeQuantities,
    allergicRecipes: reduced,
    ignoreRecipes,
  };

  const recipesMap: { [ recipe: string ]: number } = {};
  [ ...result.allowedRecipesAndQuantities.keys() ].forEach((recipe) => {
    const quantity = result.allowedRecipesAndQuantities.get(recipe) || 0;
    if (quantity > 0) {
      recipesMap[recipe] = quantity;
    }
  });

  const availableRecipes = getAvailableFreshRecipes();
  const availableOrderIndex = new Map(availableRecipes.map((r, i) => [ r, i ]));
  const allergyArr = Array.from(result.allergicRecipes)
    .filter((r) => availableOrderIndex.has(r))
    .sort((a, b) => (availableOrderIndex.get(a) ?? 0) - (availableOrderIndex.get(b) ?? 0));
  return {
    allowedRecipesAndQuantities: recipesMap,
    allergicRecipes: allergyArr,
    notAllowedRecipes: result.ignoreRecipes,
    inDefaultRecipeTest: "control",
  };
};

export const calculateRecipeQuantities = (
  defaultRecipes: Map<Recipe, number>,
  ignoreRecipes: Set<Recipe>,
  recipeCount: RecipeCount,
): Map<Recipe, number> => {
  // Fast path: no allergies affecting default recipes
  if (!ignoreRecipes || ignoreRecipes.size === 0 ||
       (!([ ...defaultRecipes.keys() ].some((r) => (defaultRecipes.get(r) || 0) > 0 && ignoreRecipes.has(r))))) {
    const toBeReturned: Map<Recipe, number> = new Map();
    defaultRecipes.forEach((quant, recipe) => {
      if (!ignoreRecipes.has(recipe)) {
        toBeReturned.set(recipe, quant);
      } else {
        toBeReturned.delete(recipe);
      }
    });
    return toBeReturned;
  }

  // Initialise all recipes with zero, then remove ignored ones
  const recipesToReturn = new Map<Recipe, number>();
  getAvailableFreshRecipes().forEach((recipe) => recipesToReturn.set(recipe, 0));
  ignoreRecipes.forEach((toBeRemoved) => {
    recipesToReturn.delete(toBeRemoved);
  });

  // Detect allergy scenario and apply allocation
  const scenario = detectAllergyScenario(ignoreRecipes);
  if (scenario !== "FALLBACK") {
    const allocation = ALLOCATIONS[recipeCount][scenario];
    Object.entries(allocation).forEach(([ recipe, quantity ]) => {
      if (quantity !== undefined) {
        recipesToReturn.set(recipe as Recipe, quantity);
      }
    });
    return recipesToReturn;
  }

  // Fallback: distribute evenly across available recipes
  const allRecipes = getAvailableFreshRecipes();
  const availableRecipes = allRecipes.filter((r) => !ignoreRecipes.has(r));
  const totalWeight = availableRecipes.length;

  if (totalWeight === 0) {
    return recipesToReturn;
  }

  let remaining = recipeCount;
  availableRecipes.forEach((recipe) => {
    const quantityToAdd = Math.floor(recipeCount / totalWeight);
    recipesToReturn.set(recipe, quantityToAdd);
    remaining -= quantityToAdd;
  });

  if (remaining > 0) {
    const firstRecipe = availableRecipes[0];
    recipesToReturn.set(firstRecipe, (recipesToReturn.get(firstRecipe) ?? 0) + remaining);
  }

  return recipesToReturn;
};

/**
 * Scenario detection rules: each scenario requires specific recipes to be available.
 * Order matters - first match wins (more specific scenarios should come first).
 */
const SCENARIO_RULES: Array<{ scenario: AllergyScenario; requiredRecipes: Recipe[] }> = [
  { scenario: "NO_ALLERGY", requiredRecipes: [ "CLUCK", "GOBBLE", "QUACK", "MOOO" ] },
  { scenario: "BEEF", requiredRecipes: [ "CLUCK", "GOBBLE", "QUACK" ] },
  { scenario: "TURKEY", requiredRecipes: [ "CLUCK", "QUACK", "MOOO" ] },
  { scenario: "DUCK", requiredRecipes: [ "CLUCK", "GOBBLE", "MOOO" ] },
  { scenario: "CHICKEN", requiredRecipes: [ "MOOO", "SPLASH", "OINK" ] },
  { scenario: "SALMON", requiredRecipes: [ "CLUCK", "GOBBLE" ] },
  { scenario: "CHICKEN_FISH", requiredRecipes: [ "MOOO" ] },
];

/**
 * Detects which allergy scenario applies based on ignored recipes.
 */
const detectAllergyScenario = (ignoreRecipes: Set<Recipe>): AllergyScenario | "FALLBACK" => {
  const isAvailable = (recipe: Recipe) => !ignoreRecipes.has(recipe);

  for (const rule of SCENARIO_RULES) {
    if (rule.requiredRecipes.every(isAvailable)) {
      return rule.scenario;
    }
  }

  return "FALLBACK";
};

const recipeAllergyRecord: Record<Recipe, Set<Allergen>> = {
  CLUCK: new Set([
    Allergen.CHICKEN,
  ]),
  GOBBLE: new Set([
    Allergen.CHICKEN,
    Allergen.TURKEY,
  ]),
  SHREDDED_CLUCK: new Set([
    Allergen.CHICKEN,
    Allergen.SALMON,
  ]),
  SHREDDED_GOBBLE: new Set([
    Allergen.CHICKEN,
    Allergen.TURKEY,
    Allergen.SALMON,
  ]),
  MOOO: new Set([
    Allergen.BEEF,
    Allergen.SALMON,
  ]),
  OINK: new Set([
    Allergen.PORK,
    Allergen.SALMON,
  ]),
  QUACK: new Set([
    Allergen.CHICKEN,
    Allergen.DUCK,
    Allergen.SALMON,
  ]),
  SPLASH: new Set([
    Allergen.TURKEY,
    Allergen.WHITEFISH,
    Allergen.SALMON,
  ]),
  BAAA: new Set([
    Allergen.BEEF,
    Allergen.LAMB,
    Allergen.SALMON,
  ]),
  SWISH: new Set([
    Allergen.TUNA,
    Allergen.SALMON,
    Allergen.CHICKEN,
  ]),
  SMOOTH_PECK: new Set([
    Allergen.CHICKEN,
    Allergen.TURKEY,
    Allergen.DUCK,
  ]),
  SMOOTH_CLUCK: new Set([
    Allergen.CHICKEN,
  ]),
};

export const allergyRecipeRecord: Record<Allergen, Set<Recipe>> =
  Object.entries(recipeAllergyRecord).reduce((((acc, [ recipe, allergens ]) => {
    allergens.forEach((allergen) => {
      acc[allergen].add(recipe as Recipe);
    });
    return acc;
  })), {
    CHICKEN: new Set(),
    WHITEFISH: new Set(),
    SALMON: new Set(),
    PORK: new Set(),
    BEEF: new Set(),
    TURKEY: new Set(),
    DUCK: new Set(),
    LAMB: new Set(),
    TUNA: new Set(),
  } satisfies typeof allergyRecipeRecord as typeof allergyRecipeRecord);
